




































import { computed, defineComponent, PropType, Ref, ref, watch } from '@nuxtjs/composition-api';
import useBreakpoint from '@shared/composables/useBreakpoint';
import { IBreakpoint } from '@shared/types/IBreakpoint';
import { BaseLink, UCIcon } from '@shared/ui';

import VNodes from '~/components/VNodes/VNodes.vue';

const MOBILE_MARGIN = '16px';

interface IConfig {
  cardMode: 'wrap' | 'scroll';
  cardSpacing: string | number;
  cardFlexBasis: string;
  cardFlexGrow: number;
  headerStyle: object;
  titleStyle: object;
  groupStyle: object;
  showDescription: boolean;
  showSteppers: boolean;
  paddingBottom: string;
  peek: boolean;
}

const defaultConfig = {
  cardMode: 'wrap',
  cardSpacing: '16px',
  cardFlexBasis: '100%',
  cardFlexGrow: 0,
  headerStyle: {},
  titleStyle: {},
  groupStyle: {},
  showDescription: true,
  showSteppers: false,
  paddingBottom: '8px',
  peek: false,
} as IConfig;

const useFlattenedConfig = (props: any, breakpoint: Ref<IBreakpoint>) => {
  const config = computed(
    () =>
      ({
        mobile: { ...defaultConfig, ...props.mobile },
        tablet: { ...defaultConfig, ...props.mobile, ...props.tablet },
        desktop: {
          ...defaultConfig,
          ...props.mobile,
          ...props.tablet,
          ...props.desktop,
        },
      }[breakpoint.value.name]),
  );

  return { config };
};

const useStyles = (config: Ref<IConfig>, breakpoint: Ref<IBreakpoint>) => {
  const styles = computed(() => {
    config.value.cardSpacing =
      typeof config.value.cardSpacing === 'number'
        ? `${config.value.cardSpacing}px`
        : config.value.cardSpacing;

    return {
      header: {
        ...config.value.headerStyle,
        ...config.value.titleStyle,
      },
      group: {
        margin:
          config.value.cardMode === 'scroll' && breakpoint.value.name === 'mobile'
            ? `0 -${MOBILE_MARGIN}`
            : '0',
        ...config.value.groupStyle,
      },
      groupInner: {
        overflowX: config.value.cardMode === 'scroll' ? 'scroll' : 'hidden',
        flexWrap: config.value.cardMode === 'wrap' ? 'wrap' : 'nowrap',
        margin: `0 calc(${config.value.cardSpacing} / -2)`,
        padding:
          config.value.cardMode === 'scroll' && breakpoint.value.name === 'mobile'
            ? `0 ${MOBILE_MARGIN}`
            : '0',
      },
      cardContainer: {
        flexBasis: config.value.cardFlexBasis,
        flexGrow: config.value.cardFlexGrow,
        padding: `calc(${config.value.cardSpacing} / 2)`,
        'padding-bottom': `${config.value.paddingBottom}`,
      },
    };
  });

  return {
    styles,
  };
};

const useVisibility = (props: any, config: Ref<IConfig>) => {
  const showDescription = computed(() => props.description && config.value.showDescription);
  const showHeaderText = computed(() => props.title || showDescription.value);

  const showSteppers = computed(
    () => config.value.cardMode === 'scroll' && config.value.showSteppers,
  );

  const showHeaderActions = computed(() => props.link || showSteppers.value);
  const showHeader = computed(() => showHeaderText.value || showHeaderActions.value);

  return {
    showDescription,
    showHeaderText,
    showSteppers,
    showHeaderActions,
    showHeader,
  };
};

const useSteppers = (groupInner: Ref<any>) => {
  const onLeftStep = () => {
    if (groupInner.value) {
      groupInner.value.scrollLeft = groupInner.value.scrollLeft - groupInner.value.clientWidth;
    }
  };

  const onRightStep = () => {
    if (groupInner.value) {
      groupInner.value.scrollLeft = groupInner.value.scrollLeft + groupInner.value.clientWidth;
    }
  };

  return {
    onLeftStep,
    onRightStep,
  };
};

export default defineComponent({
  name: 'CardSection',
  components: {
    BaseLink,
    VNodes,
    UCIcon,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    link: {
      type: [String, Object],
      default: '',
    },
    linkText: {
      type: String,
      default: '',
    },
    mobile: {
      type: Object as PropType<Partial<IConfig>>,
      default: () => ({}),
    },
    tablet: {
      type: Object as PropType<Partial<IConfig>>,
      default: () => ({}),
    },
    desktop: {
      type: Object as PropType<Partial<IConfig>>,
      default: () => ({}),
    },
  },
  setup(props) {
    const { breakpoint } = useBreakpoint();
    const { config } = useFlattenedConfig(props, breakpoint);
    const { styles } = useStyles(config, breakpoint);

    const { showDescription, showHeaderText, showSteppers, showHeaderActions, showHeader } =
      useVisibility(props, config);

    const groupInner = ref<any>(null);
    const { onLeftStep, onRightStep } = useSteppers(groupInner);

    const resetScroll = () => {
      if (groupInner.value) {
        groupInner.value.scrollLeft = 0;
      }
    };

    watch(breakpoint, resetScroll, { immediate: true });

    return {
      showDescription,
      showHeaderText,
      showSteppers,
      showHeaderActions,
      showHeader,
      styles,
      groupInner,
      onLeftStep,
      onRightStep,
    };
  },
});
